import React, { useEffect, useMemo } from 'react';
import { css } from '@emotion/css';
import formatDate from 'date-fns/format';
import parse from 'date-fns/parse';
import _debounce from 'lodash/debounce';

import { safeExec } from '@lyearn/core/utils/function';
import { Autocomplete, Input, RenderInputParams, SelectSize } from '@lyearn/ui';

import {
  convertTo12HourFormat,
  FORMAT_12H,
  FORMAT_24H,
  getTimeSelectorOptions,
  handleNativeTimePicker,
  setTimeToTargetFromDate,
} from './utils';

export { convertTo12HourFormat, FORMAT_12H, FORMAT_24H };

type FrequencyComponentProps = {
  value: Date;
  onChange: (value: Date) => void;
  className?: string;
  disabled?: boolean;
  size?: SelectSize;
};

export const hideDefaultIcon = css`
  -webkit-appearance: none;

  .MuiOutlinedInput-input {
    display: inline-flex;
  }

  input[type='time']::-webkit-calendar-picker-indicator {
    background: none;
    pointer-events: none;
  }
`;

const timeOptions = getTimeSelectorOptions({
  value: new Date(),
  timeInterval: 60,
  timeFormat: FORMAT_12H,
});

const filterOptions = (options: string[]) => options;
const getOptionLabel = (opt: string) => opt;

const TimePicker = ({
  value,
  onChange,
  className,
  disableZero,
  size,
}: FrequencyComponentProps & { className?: string; disableZero?: boolean }) => {
  const [localInputValue, setLocalInputValue] = React.useState<string>(() => {
    return formatDate(value, FORMAT_24H);
  });

  useEffect(() => {
    setLocalInputValue(formatDate(value, FORMAT_24H));
  }, [value]);

  const displayValue = convertTo12HourFormat(value);

  const onValueChange = React.useCallback(
    (_: any, v: any) => {
      const parsedDate = parse(v, FORMAT_12H, new Date());
      onChange(setTimeToTargetFromDate(value, parsedDate, { setSeconds: true }));
    },
    [onChange, value],
  );

  const onInputChangeDebounce = useMemo(
    () =>
      _debounce((text) => {
        const parsedDate = safeExec(() =>
          parse(text, text.length === 5 ? FORMAT_24H : FORMAT_12H, new Date()),
        );
        if (parsedDate) {
          onChange(setTimeToTargetFromDate(value, parsedDate));
        }
      }, 300),
    [onChange, value],
  );

  const onInputChange = React.useCallback(
    (_: any, v: any) => {
      const finalV = !disableZero ? v : v === '00:00' || v === '12:00 AM' ? '23:59' : v;
      setLocalInputValue(finalV);

      const isValidTimeFormat = finalV.length >= 5; // time format is 00:00, so trigger on change after entering full time
      if (isValidTimeFormat) {
        onInputChangeDebounce(finalV);
      }
    },
    [disableZero, onInputChangeDebounce],
  );

  return (
    <Autocomplete
      disableClearable
      forcePopupIcon
      freeSolo
      className={className}
      disableCloseOnSelect={false}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      inputValue={localInputValue}
      options={timeOptions.map((opt) => opt.label)}
      renderInput={(params: RenderInputParams) => {
        return (
          <Input
            onKeyDown={handleNativeTimePicker}
            {...params}
            className={hideDefaultIcon}
            type="time"
          />
        );
      }}
      size={size ?? 'large'}
      value={displayValue}
      onChange={onValueChange}
      onInputChange={onInputChange}
    />
  );
};

export default TimePicker;
